/**
 * @file chat-actions.ts
 * @description Реестр действий с диалогом (chat-window-design.md §4; SC62,
 * roles.md R4): пункт → подпись, значок, когда показывать.
 *
 * Показ решают признаки `actions` карточки диалога от сервера — кабинет не
 * повторяет у себя правила ролей. Переходы («Открыть заказ», «Карточка
 * клиента») — по праву на раздел, служебный пункт «Скрыть / показать
 * служебные записи» — на телефоне и в компактном окне (в окне на весь экран
 * это переключатель в шапке переписки). Новое действие = запись здесь.
 */

import {
  Archive,
  ArchiveRestore,
  ArrowRightLeft,
  Eye,
  EyeOff,
  Hash,
  Undo2,
  UserCheck,
  UserRound,
  type LucideIcon,
} from "lucide-react";

import type { ConversationDetail } from "../../lib/api-chat";
import { CHAT_TEXT } from "./chat-texts";

export type ChatActionKey =
  | "take"
  | "transfer"
  | "returnToQueue"
  | "archive"
  | "unarchive"
  | "openOrder"
  | "openCustomer"
  | "toggleService";

/** Обстоятельства вне карточки: права на разделы, ширина, служебные записи. */
export interface ChatActionContext {
  canOpenOrders: boolean;
  canOpenCustomers: boolean;
  /** Переключатель служебных записей — пунктом меню (телефон, компактное окно). */
  serviceInMenu: boolean;
  serviceShown: boolean;
}

export interface ChatActionSpec {
  key: ChatActionKey;
  label: string;
  icon: LucideIcon;
  /** Группа меню: действия с диалогом, переходы, вид. */
  group: "dialog" | "navigate" | "view";
}

interface ChatActionEntry {
  label: (ctx: ChatActionContext) => string;
  icon: (ctx: ChatActionContext) => LucideIcon;
  group: ChatActionSpec["group"];
  visible: (d: ConversationDetail, ctx: ChatActionContext) => boolean;
}

/** Порядок записей = порядок пунктов меню. */
const CHAT_ACTIONS: Record<ChatActionKey, ChatActionEntry> = {
  take: { label: () => CHAT_TEXT.take, icon: () => UserCheck, group: "dialog", visible: (d) => d.actions.take },
  transfer: { label: () => CHAT_TEXT.transfer, icon: () => ArrowRightLeft, group: "dialog", visible: (d) => d.actions.transfer },
  returnToQueue: {
    label: () => CHAT_TEXT.returnToQueue,
    icon: () => Undo2,
    group: "dialog",
    visible: (d) => d.actions.returnToQueue,
  },
  archive: { label: () => CHAT_TEXT.archive, icon: () => Archive, group: "dialog", visible: (d) => d.actions.archive },
  unarchive: {
    label: () => CHAT_TEXT.unarchive,
    icon: () => ArchiveRestore,
    group: "dialog",
    visible: (d) => d.actions.unarchive,
  },
  openOrder: {
    label: () => CHAT_TEXT.openOrder,
    icon: () => Hash,
    group: "navigate",
    visible: (d, ctx) => ctx.canOpenOrders && d.contextOrder !== null,
  },
  openCustomer: {
    label: () => CHAT_TEXT.openCustomer,
    icon: () => UserRound,
    group: "navigate",
    visible: (_d, ctx) => ctx.canOpenCustomers,
  },
  toggleService: {
    label: (ctx) => (ctx.serviceShown ? CHAT_TEXT.hideService : CHAT_TEXT.showService),
    icon: (ctx) => (ctx.serviceShown ? EyeOff : Eye),
    group: "view",
    visible: (_d, ctx) => ctx.serviceInMenu,
  },
};

/** Пункты меню «⋯» для диалога. */
export function chatMenuActions(detail: ConversationDetail, ctx: ChatActionContext): ChatActionSpec[] {
  return (Object.keys(CHAT_ACTIONS) as ChatActionKey[])
    .filter((key) => CHAT_ACTIONS[key].visible(detail, ctx))
    .map((key) => {
      const entry = CHAT_ACTIONS[key];
      return { key, label: entry.label(ctx), icon: entry.icon(ctx), group: entry.group };
    });
}

/** Кнопка рядом с «⋯» в шапке переписки: «Взять себе» или «Вернуть из архива». */
export function chatHeaderAction(detail: ConversationDetail): "take" | "unarchive" | null {
  if (detail.actions.take) return "take";
  if (detail.actions.unarchive) return "unarchive";
  return null;
}
